import "./Test.css";
import "../Animate/animate.css"

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { isAuthenticated, isStaff as checkIsStaffRole, isAdmin } from "../../api/users_institutions";
import { getTestAdminPermissions } from "../../api/admin_permissions";

import { isLevelNameLockedForUser, checkAdminLevelEditorPermission } from "../../utils/levelAccess";
import { getTopicContentById } from "../../api/topics";
import { getTopicById } from "../../api/topics";
import {fetchTestQuestionsByTest, createTestQuestion, updateTestQuestion, deleteTestQuestion} from "../../api/exams"
import {fetchTestChoicesByTest, createTestChoice, updateTestChoice, deleteTestChoice} from "../../api/exams"
import {fetchUserTestAttemptsByTest, createUserTestAttempt, updateUserTestAttempt, updateUserTestAttemptKeepalive} from "../../api/exams"
import {fetchUserTestQuestionChoicesByTest, createUserTestQuestionChoice, updateUserTestQuestionChoice, deleteUserTestQuestionChoice } from "../../api/exams";
import { getCurrentUser } from "../../api/users_institutions"
import { createTestHistoryLog } from "../../api/history_logs";
import Video from "../Video/Video";
import ModalEditorHistory from "../Modal/ModalEditorHistory";
import Chat from "../Navigation/Chat";
import Loading from "../Common/Loading";
import Toast from "../Common/Toast";
import SavingIndicator from "../Common/SavingIndicator";
import useToast from "../Common/useToast";

// How often the attempt's remaining time is synced to the backend while actively solving,
// so a hard crash (not a graceful tab close, which the pagehide keepalive already covers)
// doesn't leave the server holding a stale remaining_minutes/remaining_seconds from
// whenever the attempt was last started.
const HEARTBEAT_INTERVAL_MS = 25000;

function Test({}) {
    const { t } = useTranslation();
    const [animationClass, setAnimationClass] = useState('anim-initial');
    const typeLabel = t('exam.typeTest');
    const typeLabelLower = t('exam.typeTestLower');
    const navigate = useNavigate();
    const location = useLocation();
    const { levelName } = useParams();
    const currentUser = getCurrentUser();

    // State variables
    const [allQuestions, setAllQuestions] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [allTestsQuestionsChoices, setAllTestsQuestionsChoices] = useState([]);
    const [allUserTestAttempts, setAllUserTestAttempts] = useState([]);
    const [allUserTestQuestionChoices, setAllUserTestQuestionChoices] = useState([]);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

    // Manual-save feedback: corner spinner while the request runs, then a toast with
    // the outcome -- replaces the window.alert() this used to block on.
    const [isSavingTest, setIsSavingTest] = useState(false);
    const { toast, showToast } = useToast();

    // Exam/Test render their own top-bar instead of <Navbar>, which is where the chat
    // widget normally lives -- so the support chat is mounted here with its own state.
    const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

    const [selectedTest, setSelectedTest] = useState(location.state?.selectedTest || null);
    const [selectedTestAttempt, setSelectedTestAttempt] = useState(null);

    // An is_staff admin without their own TestAdminPermission row still gets full access as long
    // as no permission rows exist at all for this test (matching the "Leave empty for all
    // admins" semantics from ModalLevel.jsx's admin access UI); once at least one row exists,
    // only the listed admins qualify. Shadows the imported role check (aliased above) with the
    // same `isStaff()` calling convention so every existing call site picks this up for free.
    const [testAdminPermissions, setTestAdminPermissions] = useState([]);
    useEffect(() => {
        let cancelled = false;
        if (!checkIsStaffRole() || !selectedTest?.id) {
            setTestAdminPermissions([]);
            return;
        }
        getTestAdminPermissions().then((permissions) => {
            if (cancelled) return;
            const permissionsForTest = Array.isArray(permissions)
                ? permissions.filter((permission) => Number(permission.test?.id ?? permission.test) === Number(selectedTest.id))
                : [];
            setTestAdminPermissions(permissionsForTest);
        }).catch((error) => {
            console.error('Error loading test admin permissions:', error);
            if (!cancelled) setTestAdminPermissions([]);
        });
        return () => { cancelled = true; };
    }, [selectedTest?.id]);
    // Editor access for the level this test belongs to: admins/superadmins can open it under a
    // level whose category isn't aimed at "admins", but only to read it (see
    // checkAdminLevelEditorPermission). Starts false so no editing UI is offered before the check
    // resolves; folded into isStaff() below, which every editing gate on this page already runs
    // through -- including the effect that forces isEditing back off when it turns false.
    const [levelCanEdit, setLevelCanEdit] = useState(false);
    useEffect(() => {
        if (!levelName || !currentUser?.id) return;
        let cancelled = false;
        checkAdminLevelEditorPermission(levelName, currentUser.id).then((canEdit) => {
            if (!cancelled) setLevelCanEdit(canEdit);
        }).catch((error) => {
            console.error('Error checking level editor permission:', error);
            if (!cancelled) setLevelCanEdit(false);
        });
        return () => { cancelled = true; };
    }, [levelName, currentUser?.id]);
    const isStaff = useCallback(() => {
        return levelCanEdit && (isAdmin() || (checkIsStaffRole() && (
            testAdminPermissions.length === 0 ||
            testAdminPermissions.some((permission) => Number(permission.user?.id ?? permission.user) === Number(currentUser?.id))
        )));
    }, [levelCanEdit, testAdminPermissions, currentUser?.id]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [isOnlyEditing, setIsOnlyEditing] = useState(!!location.state?.isOnlyEditing);
    const [isReviewing, setIsReviewing] = useState(!!location.state?.isReviewing);
    const [isEditing, setIsEditing] = useState(isOnlyEditing);
    const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0);
    const [hasWarned5Min, setHasWarned5Min] = useState(false);
    const [hasWarned1Min, setHasWarned1Min] = useState(false);
    const [showTimeWarning, setShowTimeWarning] = useState(false);
    const [timeWarningText, setTimeWarningText] = useState("");
    const [showStartOverlay, setShowStartOverlay] = useState(!location.state?.isReviewing && !location.state?.isOnlyEditing);
    const [showFsWarning, setShowFsWarning] = useState(false);
    const [showFsSecondWarning, setShowFsSecondWarning] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [submitConfirmContent, setSubmitConfirmContent] = useState("");
    const [showMapOverlay, setShowMapOverlay] = useState(false);
    const [showEndScreen, setShowEndScreen] = useState(false);
    const [endScreenData, setEndScreenData] = useState({ title: "Test Closed", subtitle: "Your data has been saved safely.", penalty: false, score: "" });
    const [timeLimitMin, setTimeLimitMin] = useState(selectedTest?.duration || 30);
    const [timeLimitSec, setTimeLimitSec] = useState(0);
    const [isIntentionalExit, setIsIntentionalExit] = useState(false);
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(
        () => typeof window !== 'undefined' && (
            window.matchMedia('(max-width: 760px)').matches || window.matchMedia('(max-height: 500px)').matches
        )
    );

    // Editor undo/redo/save/history (mirrors Chapter.jsx's session history system): edits
    // to `questions` while isEditing are local-only until saveTestSession() diffs the
    // snapshot against sessionBaselineRef and syncs create/update/delete calls to the backend.
    const [sessionHistoryMeta, setSessionHistoryMeta] = useState({ index: -1, length: 0 });
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    useEffect(() => {
        const widthMql = window.matchMedia('(max-width: 760px)');
        const heightMql = window.matchMedia('(max-height: 500px)');
        const handleChange = () => {
            const matches = widthMql.matches || heightMql.matches;
            setIsMobileOrTablet(matches);
            if (matches) {
                setIsEditing(false);
            }
        };
        // Run once immediately too -- the listeners below only fire on a *live* crossing, so
        // loading straight into an already-short/narrow viewport wouldn't otherwise be caught.
        handleChange();
        widthMql.addEventListener('change', handleChange);
        heightMql.addEventListener('change', handleChange);
        return () => {
            widthMql.removeEventListener('change', handleChange);
            heightMql.removeEventListener('change', handleChange);
        };
    }, []);

    const timerIntervalRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    // Kept in sync with timeRemainingSeconds via an effect below so the heartbeat's
    // setInterval callback (created once, on a long cadence) always reads the current
    // value instead of the stale one captured in its closure at creation time.
    const timeRemainingSecondsRef = useRef(0);
    const activeAttemptIdRef = useRef(null);
    const highlightTooltipRef = useRef(null);
    const qTextRef = useRef(null);
    const isSeedingPlaceholderRef = useRef(false);
    const hasLoadedInitialDataRef = useRef(false);
    const isEndingTestRef = useRef(false);
    const isResizingRef = useRef(false);
    const resizeTimeoutRef = useRef(null);
    const sessionHistoryRef = useRef([]);
    const sessionHistoryIndexRef = useRef(-1);
    const sessionBaselineRef = useRef(null);
    const hasInitializedSessionRef = useRef(false);
    const isRestoringSessionRef = useRef(false);
    // Raw File objects can't survive the JSON clone used for session snapshots/undo, so
    // pending (not-yet-saved) media uploads are tracked here, keyed by question.id or
    // question._clientKey for questions created locally this session, instead of on the
    // question object itself.
    const pendingMediaFilesRef = useRef(new Map());
    const q = questions[currentIdx];

    const makeClientKey = () => `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const getQuestionMediaKey = (question) => question?._clientKey || question?.id;

    const getTestAttemptForCurrentUser = () => {
        if (!currentUser?.id || !selectedTest?.id) return null;
        return allUserTestAttempts.find(attempt => attempt.user === currentUser.id && attempt.test === selectedTest.id) || null;
    };

    const getCurrentTimeRemaining = () => {
        if (selectedTestAttempt?.completed === false && selectedTestAttempt?.remaining_minutes != null) {
            return {
                minutes: Number(selectedTestAttempt.remaining_minutes),
                seconds: Number(selectedTestAttempt.remaining_seconds || 0),
            };
        }
        return { minutes: Number(selectedTest?.duration || 30), seconds: 0 };
    };

    const syncUserAnswersFromSavedChoices = (backendQuestions, savedChoices) => {
        const answers = {};
        const savedChoiceMap = new Map();

        savedChoices
            .filter(choice => choice.user === currentUser?.id)
            .forEach(choice => {
                savedChoiceMap.set(choice.test_question, choice.selected_choice);
            });

        backendQuestions.forEach((backendQuestion, index) => {
            const selectedChoiceId = savedChoiceMap.get(backendQuestion.id);
            if (!selectedChoiceId) return;

            const selectedOptionIndex = backendQuestion.options.findIndex(option => option.choiceId === selectedChoiceId);
            if (selectedOptionIndex > -1) {
                answers[index] = [selectedOptionIndex];
            }
        });

        setUserAnswers(answers);
    };

    // All four scoped to selectedTest.id (server-side) instead of fetching every test's
    // questions/choices/attempts on the whole platform and filtering client-side.
    const fetchAlltestQuestions = async () => {
        if (!selectedTest) return;
        try {
            const questionsData = await fetchTestQuestionsByTest(selectedTest.id);
            setAllQuestions(questionsData);
        }
        catch (error) {
            console.error("Error fetching Test questions:", error);
        }
    };

    const fetchAllTestChoices = async () => {
        if (!selectedTest) return;
        try {
            const choicesData = await fetchTestChoicesByTest(selectedTest.id);
            setAllTestsQuestionsChoices(choicesData);
        }
        catch (error) {
            console.error("Error fetching Test choices:", error);
        }
    };

    const fetchAllUserTestAttempts = async () => {
        if (!selectedTest) return;
        try {
            const attemptsData = await fetchUserTestAttemptsByTest(selectedTest.id);
            setAllUserTestAttempts(attemptsData);
        } catch (error) {
            console.error('Error fetching user Test attempts:', error);
        }
    };

    const fetchAllUserTestChoices = async () => {
        if (!selectedTest) return;
        try {
            const choicesData = await fetchUserTestQuestionChoicesByTest(selectedTest.id);
            setAllUserTestQuestionChoices(choicesData);
        } catch (error) {
            console.error('Error fetching user Test question choices:', error);
        }
    };

    // Transform backend data to frontend format
    const transformQuestionsWithChoices = (backendQuestions, allChoices) => {
        return backendQuestions.map(backendQuestion => {
            const questionChoices = (allChoices && allChoices.length > 0)
                ? allChoices.filter(choice => choice.test_question === backendQuestion.id)
                : (backendQuestion.choices || []);

            const selectedChoiceRecord = allUserTestQuestionChoices.find(
                choice => choice.user === currentUser?.id && choice.test_question === backendQuestion.id
            );

            return {
                id: backendQuestion.id,
                layout: backendQuestion.layout === 'centered' ? 'center' : backendQuestion.layout,
                type: backendQuestion.choice_type, // 'single' or 'multiple'
                isFlagged: false, // frontend-only state
                mediaType: backendQuestion.mediaType,
                mediaSrc: backendQuestion.mediaSrc || '',
                localMediaSrc: backendQuestion.local_mediaSrc_url || backendQuestion.local_mediaSrc || '',
                mediaIsUpload: backendQuestion.mediaIsUpload,
                text: backendQuestion.text,
                userSelectedChoiceId: selectedChoiceRecord?.selected_choice || null,
                options: questionChoices.map(choice => ({
                    text: choice.text,
                    isCorrect: choice.is_correct,
                    choiceId: choice.id,
                    eliminated: false, // frontend-only state
                }))
            };
        });
    };

    const fetchSelectedtestQuestions = async () => {
        try {
            if (!selectedTest) {
                setQuestions([]);
                setCurrentIdx(0);
                return;
            }

            if (!hasLoadedInitialDataRef.current) {
                return;
            }

            // Filter questions for this Test
            const testQuestions = allQuestions.filter(q => q.test === selectedTest.id);

            if (testQuestions.length === 0) {
                if (isSeedingPlaceholderRef.current) {
                    return;
                }

                isSeedingPlaceholderRef.current = true;
                try {
                    const placeholderQuestion = await createTestQuestion({
                        test: selectedTest.id,
                        text: 'Which habit most helps reduce daily stress?',
                        choice_type: 'single',
                        layout: 'centered',
                        mediaType: 'none',
                        mediaSrc: null,
                        mediaIsUpload: false,
                    });

                    const placeholderChoices = await Promise.all([
                        createTestChoice({
                            test_question: placeholderQuestion.id,
                            text: 'Taking a short mindful walk',
                            is_correct: true,
                        }),
                        createTestChoice({
                            test_question: placeholderQuestion.id,
                            text: 'Ignoring stress until it builds up',
                            is_correct: false,
                        }),
                    ]);

                    const normalizedQuestion = {
                        ...placeholderQuestion,
                        choices: placeholderChoices,
                    };

                    setAllQuestions(prev => [...prev, normalizedQuestion]);
                    setAllTestsQuestionsChoices(prev => [...prev, ...placeholderChoices]);
                    const transformedQuestions = transformQuestionsWithChoices([normalizedQuestion], placeholderChoices);
                    setQuestions(transformedQuestions);
                    setCurrentIdx(0);
                } catch (seedError) {
                    console.error('Error creating placeholder Test question:', seedError);
                    setQuestions([]);
                    setCurrentIdx(0);
                } finally {
                    isSeedingPlaceholderRef.current = false;
                }
                return;
            }

            // Transform questions with their choices
            const transformedQuestions = transformQuestionsWithChoices(testQuestions, allTestsQuestionsChoices);
            setQuestions(transformedQuestions);
            setCurrentIdx(prev => Math.min(prev, Math.max(transformedQuestions.length - 1, 0)));
            syncUserAnswersFromSavedChoices(transformedQuestions, allUserTestQuestionChoices);

        } catch (error) {
            console.error("Error fetching Test questions:", error);
        }
    };

    useEffect(() => {
        if (!isAuthenticated()) {
            alert(t('exam.noPermission'));
            navigate('/');
        }
    }, [navigate]);

    // Trigger enter animation on mount (mirrors Level.jsx/Chapter.jsx's slide-in), matching the
    // slide-out-right that Level.jsx's test cards are entered from.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimationClass('anim-enter-right');
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    // Guard direct/typed/bookmarked visits to a test whose level is locked -- the normal
    // dashboard click flow already hides locked levels behind a lock icon, but that's purely a
    // rendering choice in DashboardGrid.jsx and doesn't stop someone from navigating straight to
    // the URL. Editor mode bypasses the lock, matching the level-card's own bypass. This is a
    // client-side redirect only (no backend endpoint enforces this), same as the lock icon it
    // mirrors.
    useEffect(() => {
        if (!levelName || !currentUser?.id || isAdmin()) return;
        let cancelled = false;
        isLevelNameLockedForUser(levelName, currentUser.id).then(locked => {
            if (!cancelled && locked) navigate('/dashboard');
        }).catch(error => {
            console.error('Error checking level access:', error);
        });
        return () => { cancelled = true; };
    }, [levelName, currentUser?.id, isAdmin, navigate]);

    useEffect(() => {
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        timeRemainingSecondsRef.current = timeRemainingSeconds;
    }, [timeRemainingSeconds]);

    // Fetch all Test questions and choices on component mount
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoadingQuestions(true);
                await Promise.all([
                    fetchAlltestQuestions(),
                    fetchAllTestChoices(),
                    fetchAllUserTestAttempts(),
                    fetchAllUserTestChoices(),
                ]);
            } finally {
                hasLoadedInitialDataRef.current = true;
                setIsLoadingQuestions(false);
            }
        };
        loadData();
    }, []);

    // Filter and transform questions when selectedTest or data changes
    useEffect(() => {
        fetchSelectedtestQuestions();
    }, [selectedTest, allQuestions, allTestsQuestionsChoices, allUserTestQuestionChoices]);

    const cloneSessionData = (value) => JSON.parse(JSON.stringify(value));

    const resetSessionHistory = (snapshot) => {
        const clonedSnapshot = cloneSessionData(snapshot);
        sessionHistoryRef.current = [clonedSnapshot];
        sessionHistoryIndexRef.current = 0;
        sessionBaselineRef.current = clonedSnapshot;
        setSessionHistoryMeta({ index: 0, length: 1 });
    };

    const applySessionSnapshot = (snapshot) => {
        isRestoringSessionRef.current = true;
        setQuestions(cloneSessionData(snapshot));
        // Undo/redo can change the question count (e.g. undoing an add/delete); keep
        // currentIdx pointing at a real question so `q = questions[currentIdx]` never
        // reads undefined and crashes the render.
        setCurrentIdx(prev => Math.min(prev, Math.max(0, snapshot.length - 1)));
    };

    const handleUndo = () => {
        if (sessionHistoryIndexRef.current <= 0) return;
        const nextIndex = sessionHistoryIndexRef.current - 1;
        sessionHistoryIndexRef.current = nextIndex;
        setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
        applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
    };

    const handleRedo = () => {
        if (sessionHistoryIndexRef.current >= sessionHistoryRef.current.length - 1) return;
        const nextIndex = sessionHistoryIndexRef.current + 1;
        sessionHistoryIndexRef.current = nextIndex;
        setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
        applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
    };

    const logTestChange = async (actionTitle, additionalData = {}) => {
        if (!selectedTest?.id) return;
        try {
            await createTestHistoryLog({
                test: selectedTest.id,
                log_title: actionTitle,
                log_data: additionalData,
            });
        } catch (err) {
            console.error('Error logging test change:', err);
        }
    };

    // Establishes the undo/redo baseline the first time real questions load, then
    // captures a new snapshot on every edit made while in editor mode. Test-taking-only
    // state changes (flagging, elimination) don't get captured since isEditing is false then.
    useEffect(() => {
        if (!questions.length) return;

        if (!hasInitializedSessionRef.current) {
            hasInitializedSessionRef.current = true;
            resetSessionHistory(questions);
            return;
        }

        if (!isEditing) return;

        if (isRestoringSessionRef.current) {
            isRestoringSessionRef.current = false;
            return;
        }

        const snapshot = cloneSessionData(questions);
        const lastEntry = sessionHistoryRef.current[sessionHistoryIndexRef.current];
        if (lastEntry && JSON.stringify(lastEntry) === JSON.stringify(snapshot)) return;

        sessionHistoryRef.current = sessionHistoryRef.current.slice(0, sessionHistoryIndexRef.current + 1);
        sessionHistoryRef.current.push(snapshot);
        sessionHistoryIndexRef.current = sessionHistoryRef.current.length - 1;
        setSessionHistoryMeta({
            index: sessionHistoryIndexRef.current,
            length: sessionHistoryRef.current.length,
        });
    }, [questions, isEditing]);

    // Warn before leaving/refreshing the page if there are unsaved editor changes.
    useEffect(() => {
        if (!isStaff()) return;

        const handleBeforeUnload = (e) => {
            if (isEditing && sessionHistoryIndexRef.current > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isEditing]);

    useEffect(() => {
        const attempt = getTestAttemptForCurrentUser();
        setSelectedTestAttempt(attempt);

        if (attempt && attempt.completed === false && attempt.remaining_minutes != null) {
            setTimeLimitMin(Number(attempt.remaining_minutes));
            setTimeLimitSec(Number(attempt.remaining_seconds || 0));
        } else if (selectedTest?.duration != null) {
            setTimeLimitMin(Number(selectedTest.duration));
            setTimeLimitSec(0);
        }
    }, [selectedTest, allUserTestAttempts]);

    useEffect(() => {
        if (isReviewing) {
            setShowStartOverlay(false);
            setIsEditing(false);
        }
    }, [isReviewing]);

    useEffect(() => {
        if (isOnlyEditing) {
            setShowStartOverlay(false);
            setIsEditing(true);
            document.body.classList.toggle('is-editing', true);
        }
    }, [isOnlyEditing])

    useEffect(() => {
        if (!isStaff() || isReviewing || isMobileOrTablet) {
            setIsEditing(prev => {
                document.body.classList.toggle('is-editing', false);
                setShowStartOverlay(!isReviewing);
                return false;
            });
        } else if (isOnlyEditing) {
            // Re-enter edit mode once the level permission check resolves. An "edit this test" deep
            // link turns isEditing on in the isOnlyEditing effect above, but this effect has
            // already forced it back off while levelCanEdit was still unknown, and the effect
            // above won't re-run on its own -- without this branch the admin lands on the start
            // overlay instead of the editor.
            document.body.classList.toggle('is-editing', true);
            setShowStartOverlay(false);
            setIsEditing(true);
        }
    }, [isStaff, isReviewing, isMobileOrTablet, isOnlyEditing]);

    // Timer Logic
    const startTimer = () => {
        if (isReviewing) return;

        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }

        setHasWarned5Min(false);
        setHasWarned1Min(false);

        const totalSeconds = (timeLimitMin * 60) + timeLimitSec;
        if (totalSeconds <= 0) return;

        setTimeRemainingSeconds(totalSeconds);

        timerIntervalRef.current = setInterval(() => {
            setTimeRemainingSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(timerIntervalRef.current);
                    triggerEndTest('timeout');
                    return 0;
                }

                const newTime = prev - 1;

                if (newTime === 300) {
                    displayTimeWarning(t('exam.reminder5Min'));
                    setHasWarned5Min(true);
                }

                if (newTime === 60) {
                    displayTimeWarning(t('exam.reminder1Min'));
                    setHasWarned1Min(true);
                }

                return newTime;
            });
        }, 1000);
    };

    const stopTimer = () => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }
    };

    // Heartbeat: periodically syncs remaining_minutes/remaining_seconds to the backend
    // while actively solving, so a hard crash (process killed, power loss - anything that
    // doesn't fire the pagehide keepalive) doesn't leave the server holding a stale
    // remaining time from whenever the attempt was last started. Sends a partial PUT
    // payload; score/passed/attempt_count/penalty_count all have model defaults, so DRF
    // treats them as optional and leaves the existing values untouched when omitted.
    const sendTestHeartbeat = async () => {
        if (!activeAttemptIdRef.current || !currentUser?.id || !selectedTest?.id) return;

        const totalSeconds = Math.max(0, timeRemainingSecondsRef.current);
        try {
            const updatedAttempt = await updateUserTestAttempt(activeAttemptIdRef.current, {
                user: currentUser.id,
                test: selectedTest.id,
                is_solving: true,
                completed: false,
                remaining_minutes: Math.floor(totalSeconds / 60),
                remaining_seconds: totalSeconds % 60,
            });
            if (updatedAttempt) {
                setSelectedTestAttempt(updatedAttempt);
                setAllUserTestAttempts(prev => prev.map(attempt => attempt.id === updatedAttempt.id ? updatedAttempt : attempt));
            }
        } catch (error) {
            console.error('Error sending Test heartbeat:', error);
        }
    };

    const startHeartbeat = () => {
        stopHeartbeat();
        heartbeatIntervalRef.current = setInterval(sendTestHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const stopHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
        activeAttemptIdRef.current = null;
    };

    const displayTimeWarning = (text) => {
        setTimeWarningText(text);
        setShowTimeWarning(true);
        setTimeout(() => setShowTimeWarning(false), 7000);
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const getDisplayedTimeRemaining = () => {
        const totalSeconds = Math.max(0, timeRemainingSeconds);
        return {
            minutes: Math.floor(totalSeconds / 60),
            seconds: totalSeconds % 60,
        };
    };

    const getAnsweredCount = () => {
        return Object.keys(userAnswers).filter(k => userAnswers[k] && userAnswers[k].length > 0).length;
    };

    const isQuestionAnsweredCorrectly = (questionIndex) => {
        const question = questions[questionIndex];
        const selected = (userAnswers[questionIndex] || []).slice().sort((a, b) => a - b);
        const correct = question.options
            .map((option, idx) => (option.isCorrect ? idx : null))
            .filter(idx => idx !== null)
            .sort((a, b) => a - b);

        if (selected.length !== correct.length) return false;
        return selected.every((value, idx) => value === correct[idx]);
    };

    const calculateTestScore = (reason) => {
        const totalQuestions = questions.length || 0;
        const maxScore = Number(selectedTest?.max_score || selectedTest?.total_score || 0);
        if (totalQuestions === 0 || maxScore <= 0) return 0;

        const pointsPerQuestion = maxScore / totalQuestions;
        const penaltyCount = Number(selectedTestAttempt?.penalty_count || 0) + (reason === 'penalty' ? 1 : 0);
        let score = 0;

        questions.forEach((_, idx) => {
            if (isQuestionAnsweredCorrectly(idx)) {
                score += pointsPerQuestion;
            }
        });

        if (penaltyCount > 0) {
            score = score - (maxScore * 0.1 * penaltyCount);
        }

        return Math.max(0, Math.round(score * 100) / 100);
    };

    const persistTestAttempt = async ({ score, completed = false, isSolving = false, remainingMinutes = null, remainingSeconds = null, penaltyCount = 0, resetSavedChoices = false }) => {
        if (!currentUser?.id || !selectedTest?.id) return null;

        const passed = score >= (Number(selectedTest.max_score || selectedTest.total_score || 0) / 2);

        try {
            const attempts = await fetchUserTestAttemptsByTest(selectedTest.id);
            const existingAttempt = attempts.find(
                attempt => attempt.user === currentUser.id && attempt.test === selectedTest.id
            );

            if (resetSavedChoices && existingAttempt) {
                const currentTestQuestionIds = new Set((questions || []).map(question => question.id));
                const savedChoices = allUserTestQuestionChoices.filter(
                    choice => choice.user === currentUser.id && currentTestQuestionIds.has(choice.test_question)
                );

                await Promise.all(savedChoices.map(choice => deleteUserTestQuestionChoice(choice.id)));
                setAllUserTestQuestionChoices(prev => prev.filter(choice => !savedChoices.some(savedChoice => savedChoice.id === choice.id)));
                setQuestions(prev => prev.map(question => ({ ...question, userSelectedChoiceId: null })));
                setUserAnswers({});
            }

            const attemptPayload = {
                user: currentUser.id,
                test: selectedTest.id,
                score,
                passed,
                attempt_count: Number(existingAttempt?.attempt_count || 0) + 1,
                completed,
                is_solving: isSolving,
                penalty_count: penaltyCount,
                remaining_minutes: remainingMinutes,
                remaining_seconds: remainingSeconds,
            };

            if (existingAttempt) {
                const updatedAttempt = await updateUserTestAttempt(existingAttempt.id, attemptPayload);
                setAllUserTestAttempts(prev => prev.map(attempt => attempt.id === existingAttempt.id ? updatedAttempt : attempt));
                setSelectedTestAttempt(updatedAttempt);
                return updatedAttempt;
            }

            const createdAttempt = await createUserTestAttempt(attemptPayload);
            setAllUserTestAttempts(prev => [...prev, createdAttempt]);
            setSelectedTestAttempt(createdAttempt);
            return createdAttempt;
        } catch (error) {
            console.error('Error saving Test attempt:', error);
            return null;
        }
    };

    // Fullscreen Logic
    const requestFullScreen = async () => {
        try {
            if (!document.fullscreenElement) {
                await document.documentElement.requestFullscreen();
            }
        } catch (err) {
            console.log(`Error attempting to enable fullscreen: ${err.message}`);
        }
    };

    const startTest = async () => {
        if (isReviewing) return;

        isEndingTestRef.current = false;
        setShowStartOverlay(false);
        await requestFullScreen();
        const attempt = getTestAttemptForCurrentUser();
        const hasSavedRemaining = attempt && attempt.completed === false && attempt.remaining_minutes != null;
        const remainingMinutes = hasSavedRemaining ? Number(attempt.remaining_minutes) : Number(selectedTest?.duration || 30);
        const remainingSeconds = hasSavedRemaining ? Number(attempt.remaining_seconds || 0) : 0;

        const persistedAttempt = await persistTestAttempt({
            score: Number(attempt?.score || 0),
            completed: false,
            isSolving: true,
            remainingMinutes,
            remainingSeconds,
            penaltyCount: Number(attempt?.penalty_count || 0),
            resetSavedChoices: !!location.state?.resetUserTestChoices,
        });
        activeAttemptIdRef.current = persistedAttempt?.id || null;
        startTimer();
        startHeartbeat();
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!isEditing && !document.fullscreenElement && !isIntentionalExit && !showStartOverlay) {
                setShowFsWarning(true);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [isEditing, isIntentionalExit, showStartOverlay]);

    const canEndForFocusLoss = () => (
        !isEditing &&
        !isReviewing &&
        !showStartOverlay &&
        !showEndScreen &&
        !isIntentionalExit &&
        timeRemainingSeconds > 0 &&
        !isEndingTestRef.current
    );

    // Dragging/snapping the browser window can momentarily blur it; don't let
    // that count as the user tabbing away and trigger a penalty.
    useEffect(() => {
        const handleResize = () => {
            isResizingRef.current = true;
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
            resizeTimeoutRef.current = setTimeout(() => {
                isResizingRef.current = false;
            }, 500);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const endForPenalty = () => {
            if (isResizingRef.current) return;
            if (!canEndForFocusLoss()) return;
            isEndingTestRef.current = true;
            triggerEndTest('penalty');
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                endForPenalty();
            }
        };

        window.addEventListener('blur', endForPenalty);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('blur', endForPenalty);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isEditing, isReviewing, showStartOverlay, showEndScreen, isIntentionalExit, timeRemainingSeconds]);

    // Regular (non-keepalive) requests get cancelled once the tab actually
    // closes, so persist the penalty with a keepalive fetch on pagehide.
    useEffect(() => {
        const handlePageHide = () => {
            if (!canEndForFocusLoss()) return;

            const existingAttempt = getTestAttemptForCurrentUser();
            if (!existingAttempt?.id || !currentUser?.id || !selectedTest?.id) return;

            isEndingTestRef.current = true;

            const calculatedScore = calculateTestScore('penalty');
            const passed = calculatedScore >= (Number(selectedTest.max_score || selectedTest.total_score || 0) / 2);
            const remaining = getDisplayedTimeRemaining();

            updateUserTestAttemptKeepalive(existingAttempt.id, {
                user: currentUser.id,
                test: selectedTest.id,
                score: calculatedScore,
                passed,
                attempt_count: Number(existingAttempt.attempt_count || 0) + 1,
                completed: false,
                is_solving: false,
                penalty_count: Number(existingAttempt.penalty_count || 0) + 1,
                remaining_minutes: remaining.minutes,
                remaining_seconds: remaining.seconds,
            });
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [isEditing, isReviewing, showStartOverlay, showEndScreen, isIntentionalExit, timeRemainingSeconds, selectedTest, allUserTestAttempts, questions, userAnswers, currentUser]);

    // Edit Mode Logic
    const toggleEditMode = () => {
        if (isMobileOrTablet) return;

        if (!isStaff() || isReviewing) {
            alert(t('exam.noPermissionEditor'));
            return;
        }

        setIsEditing(prev => {
            if (isEditing) {
                document.body.classList.toggle('is-editing', false);
                setShowStartOverlay(true);
            } else {
                document.body.classList.toggle('is-editing', true);
                triggerEndTest('edit mode');
                stopTimer();
            }

            return !prev;
        });
    };

    // Question Management
    const deleteCurrentQuestion = () => {
        if (questions.length <= 1) {
            alert(t('exam.cannotDeleteLastQuestion'));
            return;
        }

        if (!window.confirm(t('exam.confirmDeleteQuestion'))) return;

        deleteQuestionFromBackend();
    };

    // Question/choice edits below are local-only (mirroring Chapter.jsx's editor model):
    // they mutate `questions` state and log an audit entry immediately, but the actual
    // backend sync only happens in saveTestSession(). Undo/redo just replay local snapshots.
    const updateProp = (prop, value) => {
        if (isReviewing || !isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            const currentQuestion = newQuestions[currentIdx];
            let updatedQuestion = { ...currentQuestion, [prop]: value };

            // Switching from multi-select to single-select: a single-choice question can only
            // have one correct answer, so collapse down to just the first (top-most) option that
            // was already marked correct, rather than silently leaving several checked.
            if (prop === 'type' && value === 'single' && currentQuestion.type === 'multiple') {
                const firstCorrectIdx = currentQuestion.options.findIndex(opt => opt.isCorrect);
                updatedQuestion = {
                    ...updatedQuestion,
                    options: currentQuestion.options.map((opt, i) => ({
                        ...opt,
                        isCorrect: firstCorrectIdx === -1 ? i === 0 : i === firstCorrectIdx,
                    })),
                };
            }

            newQuestions[currentIdx] = updatedQuestion;
            return newQuestions;
        });
        logTestChange(`Updated ${prop}`, { field: prop, new_value: value });
    };

    const updateMedia = (type) => {
        if (isReviewing || !isEditing) return;

        pendingMediaFilesRef.current.delete(getQuestionMediaKey(questions[currentIdx]));
        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx] = {
                ...newQuestions[currentIdx],
                mediaType: type,
                mediaSrc: '',
                localMediaSrc: '',
                mediaIsUpload: false,
            };
            return newQuestions;
        });
        logTestChange('Updated media type', { media_type: type });
    };

    const saveMediaUrl = (url) => {
        if (isReviewing || !isEditing) return;

        pendingMediaFilesRef.current.delete(getQuestionMediaKey(questions[currentIdx]));
        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx] = {
                ...newQuestions[currentIdx],
                mediaSrc: url,
                localMediaSrc: '',
                mediaIsUpload: false,
            };
            return newQuestions;
        });
        logTestChange('Updated media URL');
    };

    const handleFileUpload = (file) => {
        if (!file || isReviewing || !isEditing) return;

        const previewUrl = URL.createObjectURL(file);
        pendingMediaFilesRef.current.set(getQuestionMediaKey(questions[currentIdx]), file);
        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx] = {
                ...newQuestions[currentIdx],
                mediaSrc: '',
                localMediaSrc: previewUrl,
                mediaIsUpload: true,
            };
            return newQuestions;
        });
        logTestChange('Uploaded media file');
    };

    const toggleFlag = () => {
        if (isReviewing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx].isFlagged = !newQuestions[currentIdx].isFlagged;
            return newQuestions;
        });
    };

    const toggleEliminate = (oIdx) => {
        if (isReviewing || isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx].options[oIdx].eliminated = !newQuestions[currentIdx].options[oIdx].eliminated;
            return newQuestions;
        });

        if (questions[currentIdx].options[oIdx].eliminated) {
            setUserAnswers(prev => {
                const newAnswers = { ...prev };
                if (newAnswers[currentIdx]) {
                    const idxInArr = newAnswers[currentIdx].indexOf(oIdx);
                    if (idxInArr > -1) {
                        newAnswers[currentIdx].splice(idxInArr, 1);
                    }
                }
                return newAnswers;
            });
        }
    };

    const toggleUserAnswer = (oIdx) => {
        const q = questions[currentIdx];

        if (isEditing || isReviewing) return;

        const selectedChoice = q?.options?.[oIdx];
        if (!q?.id || !selectedChoice?.choiceId || !currentUser?.id) return;

        const existingChoice = allUserTestQuestionChoices.find(
            choice => choice.user === currentUser.id && choice.test_question === q.id
        );

        const syncLocalAnswers = (shouldClear = false) => {
            setUserAnswers(prev => {
                const newAnswers = { ...prev };
                if (shouldClear) {
                    delete newAnswers[currentIdx];
                    return newAnswers;
                }

                if (q.type === 'single') {
                    newAnswers[currentIdx] = [oIdx];
                } else {
                    if (!newAnswers[currentIdx]) newAnswers[currentIdx] = [];
                    const idxInArr = newAnswers[currentIdx].indexOf(oIdx);
                    if (idxInArr > -1) {
                        newAnswers[currentIdx].splice(idxInArr, 1);
                    } else {
                        newAnswers[currentIdx].push(oIdx);
                    }
                }
                return newAnswers;
            });
        };

        const saveSelectedChoice = async () => {
            try {
                if (existingChoice && existingChoice.selected_choice === selectedChoice.choiceId) {
                    await deleteUserTestQuestionChoice(existingChoice.id);
                    setAllUserTestQuestionChoices(prev => prev.filter(choice => choice.id !== existingChoice.id));
                    syncLocalAnswers(true);
                    return;
                }

                if (existingChoice) {
                    const updatedChoice = await updateUserTestQuestionChoice(existingChoice.id, {
                        user: currentUser.id,
                        test_question: q.id,
                        selected_choice: selectedChoice.choiceId,
                    });
                    setAllUserTestQuestionChoices(prev => prev.map(choice => choice.id === existingChoice.id ? updatedChoice : choice));
                } else {
                    const createdChoice = await createUserTestQuestionChoice({
                        user: currentUser.id,
                        test_question: q.id,
                        selected_choice: selectedChoice.choiceId,
                    });
                    setAllUserTestQuestionChoices(prev => [...prev, createdChoice]);
                }

                syncLocalAnswers(false);
            } catch (error) {
                console.error('Error saving Test answer:', error);
            }
        };

        saveSelectedChoice();
    };

    const toggleCorrectAnswer = (oIdx) => {
        if (isReviewing || !isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            const q = { ...newQuestions[currentIdx], options: newQuestions[currentIdx].options.map(opt => ({ ...opt })) };
            if (q.type === 'single') {
                // Exclusive selection -- clicking any option (including the already-correct one)
                // just moves/reconfirms the single check mark there. There's always exactly one
                // correct option for a single-choice question, so it can never be unchecked down
                // to zero.
                q.options.forEach((opt, i) => {
                    opt.isCorrect = (i === oIdx);
                });
            } else {
                const correctCount = q.options.filter(opt => opt.isCorrect).length;
                const isOnlyRemainingCorrect = q.options[oIdx].isCorrect && correctCount === 1;
                // At least one answer must always stay marked correct -- clicking the last
                // remaining one is a no-op instead of leaving none checked.
                if (!isOnlyRemainingCorrect) {
                    q.options[oIdx].isCorrect = !q.options[oIdx].isCorrect;
                }
            }
            newQuestions[currentIdx] = q;
            return newQuestions;
        });
        logTestChange('Updated correct answer');
    };

    const addOption = () => {
        if (isReviewing || !isEditing) return;

        const question = questions[currentIdx];
        if (!question) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx].options.push({
                choiceId: null,
                text: 'New Option',
                isCorrect: false,
                eliminated: false,
            });
            return newQuestions;
        });
        logTestChange('Added answer option');
    };

    const deleteOption = (oIdx) => {
        if (isReviewing || !isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            const options = newQuestions[currentIdx].options.filter((_, i) => i !== oIdx);
            // Deleting the only correct option would leave none marked -- promote the first
            // remaining one so there's always at least one correct answer.
            if (options.length > 0 && !options.some(opt => opt.isCorrect)) {
                options[0] = { ...options[0], isCorrect: true };
            }
            newQuestions[currentIdx] = { ...newQuestions[currentIdx], options };
            return newQuestions;
        });
        logTestChange('Deleted answer option');
    };

    const saveText = (text) => {
        if (isReviewing || !isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx].text = text;
            return newQuestions;
        });
        logTestChange('Updated question text');
    };

    const saveChoiceText = (choiceIndex, text) => {
        if (isReviewing || !isEditing) return;

        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions[currentIdx].options[choiceIndex].text = text;
            return newQuestions;
        });
        logTestChange('Updated answer option text');
    };

    const addNewQuestion = () => {
        if (isReviewing || !isEditing || !selectedTest?.id) return;

        setQuestions(prev => [
            ...prev,
            {
                id: null,
                _clientKey: makeClientKey(),
                layout: 'split',
                type: 'single',
                isFlagged: false,
                mediaType: 'none',
                mediaSrc: '',
                localMediaSrc: '',
                mediaIsUpload: false,
                text: 'New Question Text...',
                options: [
                    // At least one option must always be marked correct.
                    { choiceId: null, text: 'Option A', isCorrect: true, eliminated: false },
                    { choiceId: null, text: 'Option B', isCorrect: false, eliminated: false },
                ],
            },
        ]);
        setCurrentIdx(questions.length);
        logTestChange('Added question');
    };

    const deleteQuestionFromBackend = () => {
        if (isReviewing || !isEditing) return;

        const question = questions[currentIdx];
        if (!question) return;

        pendingMediaFilesRef.current.delete(getQuestionMediaKey(question));
        setQuestions(prev => {
            const newQuestions = [...prev];
            newQuestions.splice(currentIdx, 1);
            return newQuestions;
        });
        setUserAnswers(prev => {
            const newAnswers = {};
            for (let key in prev) {
                const numKey = parseInt(key);
                if (numKey < currentIdx) {
                    newAnswers[numKey] = prev[numKey];
                } else if (numKey > currentIdx) {
                    newAnswers[numKey - 1] = prev[numKey];
                }
            }
            return newAnswers;
        });
        setCurrentIdx(prev => Math.max(0, prev - 1));
        logTestChange('Deleted question', { question_text: question.text });
    };

    // Diffs the current local `questions` snapshot against sessionBaselineRef and syncs
    // every create/update/delete to the backend in one go (mirrors Chapter.jsx's
    // saveChapterSession). New questions/choices carry id: null / choiceId: null locally
    // until this resolves them to real backend ids.
    const saveTestSession = async () => {
        // Ctrl+S and the toolbar button share this handler; a second trigger mid-save
        // would re-post every create/delete in the diff.
        if (!isStaff() || !isEditing || isSavingTest) return;

        const snapshot = cloneSessionData(questions);
        const baseline = sessionBaselineRef.current || snapshot;
        const allCurrentQuestionIds = new Set();
        snapshot.forEach(question => { if (question.id) allCurrentQuestionIds.add(question.id); });

        const syncChoices = async (questionSnapshot, questionBaseline) => {
            const currentChoices = questionSnapshot.options || [];
            const baselineChoices = questionBaseline?.options || [];
            const currentChoiceIds = new Set(currentChoices.map(choice => choice.choiceId).filter(Boolean));

            for (const choice of currentChoices) {
                const payload = {
                    test_question: questionSnapshot.id,
                    text: choice.text || '',
                    is_correct: !!choice.isCorrect,
                };
                if (choice.choiceId) {
                    await updateTestChoice(choice.choiceId, payload);
                } else {
                    const created = await createTestChoice(payload);
                    choice.choiceId = created.id;
                }
            }

            for (const choice of baselineChoices) {
                if (choice.choiceId && !currentChoiceIds.has(choice.choiceId)) {
                    await deleteTestChoice(choice.choiceId);
                }
            }
        };

        setIsSavingTest(true);
        try {
            for (const question of snapshot) {
                const mediaKey = getQuestionMediaKey(question);
                const pendingFile = pendingMediaFilesRef.current.get(mediaKey);
                const payload = {
                    test: selectedTest?.id,
                    text: question.text || '',
                    choice_type: question.type || 'single',
                    layout: question.layout === 'center' ? 'centered' : (question.layout || 'left'),
                    mediaType: question.mediaType || 'none',
                    mediaSrc: question.mediaSrc || null,
                    mediaIsUpload: !!question.mediaIsUpload,
                };

                if (question.id) {
                    const updatePayload = pendingFile ? { ...payload, local_mediaSrc: pendingFile } : payload;
                    const updated = await updateTestQuestion(question.id, updatePayload);
                    if (updated) {
                        question.mediaSrc = updated.mediaSrc || '';
                        question.localMediaSrc = updated.local_mediaSrc_url || updated.local_mediaSrc || '';
                        question.mediaIsUpload = !!updated.mediaIsUpload;
                    }
                } else {
                    const created = await createTestQuestion(payload);
                    question.id = created.id;
                    allCurrentQuestionIds.add(question.id);

                    if (pendingFile) {
                        const updated = await updateTestQuestion(question.id, { ...payload, local_mediaSrc: pendingFile });
                        if (updated) {
                            question.mediaSrc = updated.mediaSrc || '';
                            question.localMediaSrc = updated.local_mediaSrc_url || updated.local_mediaSrc || '';
                            question.mediaIsUpload = !!updated.mediaIsUpload;
                        }
                    }
                }
                pendingMediaFilesRef.current.delete(mediaKey);

                const baselineQuestion = baseline.find(item => item.id === question.id) || { options: [] };
                await syncChoices(question, baselineQuestion);
            }

            for (const question of baseline) {
                if (question.id && !allCurrentQuestionIds.has(question.id)) {
                    await deleteTestQuestion(question.id);
                }
            }

            isRestoringSessionRef.current = true;
            setQuestions(cloneSessionData(snapshot));
            resetSessionHistory(snapshot);
            showToast(t('exam.changesSaved'));
        } catch (err) {
            console.error('Failed to save test session:', err);
            showToast(t('exam.errors.saveChangesFailed'), 'error');
        } finally {
            setIsSavingTest(false);
        }
    };

    // Keyboard shortcuts for save/undo/redo while editing. handleUndo/handleRedo/
    // saveTestSession are plain functions re-created every render (not memoized), closing
    // over the current questions/currentIdx/selectedTest - they must be in the deps so this
    // listener re-attaches with a fresh closure on every render. Without them, Ctrl+S/Z/Y
    // keeps calling whatever version of these functions existed when isEditing last toggled,
    // operating on stale `questions` and clobbering newer edits when it saves.
    useEffect(() => {
        if (!isStaff() || !isEditing) return;

        const handleKeyDown = (e) => {
            const activeEl = document.activeElement;
            const isEditingText = activeEl && (activeEl.isContentEditable || ['INPUT', 'TEXTAREA'].includes(activeEl.tagName));
            if (isEditingText) return;

            const isCtrlOrCmd = e.ctrlKey || e.metaKey;

            if (isCtrlOrCmd && (e.key === 'z' || e.key === 'Z')) {
                e.preventDefault();
                handleUndo();
            } else if (isCtrlOrCmd && (e.key === 'y' || e.key === 'Y')) {
                e.preventDefault();
                handleRedo();
            } else if (isCtrlOrCmd && (e.key === 's' || e.key === 'S')) {
                e.preventDefault();
                saveTestSession();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isStaff, isEditing, handleUndo, handleRedo, saveTestSession]);

    const navigate_question = (dir) => {
        const newIdx = currentIdx + dir;
        if (newIdx >= 0 && newIdx < questions.length) {
            setCurrentIdx(newIdx);
        }
    };

    const openMap = () => {
        setShowMapOverlay(true);
    };

    const closeMap = (e) => {
        if (e && !e.target.classList.contains('overlay') && !e.target.closest('.map-box')) return;
        setShowMapOverlay(false);
    };

    const jumpToQuestion = (idx) => {
        setCurrentIdx(idx);
        setShowMapOverlay(false);
    };

    const jumpToFromSubmit = (idx) => {
        setCurrentIdx(idx);
        setShowSubmitConfirm(false);
    };

    // Submit/End Test Logic
    const confirmSubmit = () => {
        const unanswered = [];
        const flagged = [];

        questions.forEach((q, i) => {
            const isAnswered = userAnswers[i] && userAnswers[i].length > 0;
            if (!isAnswered) unanswered.push(i);
            if (q.isFlagged) flagged.push(i);
        });

        const answeredCount = questions.length - unanswered.length;
        const total = questions.length;

        let content = `<p style="margin-bottom: 15px;">${t('exam.submitSummary', { answered: answeredCount, total })}</p>`;

        if (unanswered.length > 0 || flagged.length > 0) {
            content += `<div class="submit-warnings-container">`;

            if (unanswered.length > 0) {
                content += `<div class="submit-warning-title">${t('exam.unansweredQuestions')}</div>`;
                content += `<div class="submit-mini-grid">`;
                unanswered.forEach(idx => {
                    content += `<div class="grid-cell" onclick="window.jumpToFromSubmit(${idx})">${idx + 1}</div>`;
                });
                content += `</div>`;
            }

            if (flagged.length > 0) {
                content += `<div class="submit-warning-title" style="color: #b45309; margin-top: 10px;">${t('exam.flaggedQuestions')}</div>`;
                content += `<div class="submit-mini-grid">`;
                flagged.forEach(idx => {
                    content += `<div class="grid-cell flagged" onclick="window.jumpToFromSubmit(${idx})">${idx + 1}</div>`;
                });
                content += `</div>`;
            }

            content += `</div>`;
            content += `<p style="margin: 0; font-size: 0.95rem; font-weight: 600; color: #ef4444;">${t('exam.confirmSubmitAnyway')}</p>`;
        } else {
            content += `<p style="margin-top: 15px;">${t('exam.confirmReadySubmit', { typeLower: typeLabelLower })}</p>`;
        }

        setSubmitConfirmContent(content);
        setShowSubmitConfirm(true);
    };

    const cancelSubmit = () => {
        setShowSubmitConfirm(false);
    };

    const cancelFirstWarning = async () => {
        setShowFsWarning(false);
        await requestFullScreen();
    };

    const triggerSecondWarning = () => {
        setShowFsWarning(false);
        setShowFsSecondWarning(true);
    };

    const cancelSecondWarning = async () => {
        setShowFsSecondWarning(false);
        await requestFullScreen();
    };

    const triggerEndTest = async (reason) => {
        isEndingTestRef.current = true;
        setIsIntentionalExit(true);
        stopTimer();
        stopHeartbeat();

        setShowFsWarning(false);
        setShowFsSecondWarning(false);
        setShowSubmitConfirm(false);

        const answered = getAnsweredCount();
        const total = questions.length;

        let title, subtitle, showPenalty = false, score = "";
        const existingAttempt = getTestAttemptForCurrentUser();
        const nextPenaltyCount = reason === 'penalty'
            ? Number(existingAttempt?.penalty_count || 0) + 1
            : Number(existingAttempt?.penalty_count || 0);

        if (reason === 'penalty') {
            title = t('exam.endScreens.penaltyTitle', { type: typeLabel });
            subtitle = t('exam.endScreens.penaltySubtitle', { typeLower: typeLabelLower });
            showPenalty = true;
        }
        else if (reason === 'edit mode') {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(e => console.log(e));
            }
            return;
        } else if (reason === 'timeout') {
            title = t('exam.endScreens.timeoutTitle');
            subtitle = t('exam.endScreens.timeoutSubtitle');
        } else if (reason === 'submit') {
            title = t('exam.endScreens.submitTitle', { type: typeLabel });
            subtitle = t('exam.endScreens.submitSubtitle');
        }

        const calculatedScore = calculateTestScore(reason);
        const shouldComplete = reason === 'submit' || reason === 'timeout';
        const remainingToSave = reason === 'penalty' ? getDisplayedTimeRemaining() : (shouldComplete ? null : getCurrentTimeRemaining());
        await persistTestAttempt({
            score: calculatedScore,
            completed: shouldComplete,
            isSolving: false,
            remainingMinutes: remainingToSave ? remainingToSave.minutes : null,
            remainingSeconds: remainingToSave ? remainingToSave.seconds : null,
            penaltyCount: shouldComplete ? 0 : nextPenaltyCount,
        });
        score = reason === 'penalty'
            ? ""
            : t('exam.scoreDisplay', { score: calculatedScore.toFixed(2), max: Number(selectedTest?.max_score || 0).toFixed(2) });

        setEndScreenData({ title, subtitle, penalty: showPenalty, score });
        setShowEndScreen(true);

        if (document.fullscreenElement) {
            document.exitFullscreen().catch(e => console.log(e));
        }

        const currentTopicContent = selectedTest?.topic_content ? await getTopicContentById(selectedTest.topic_content) : null;
        const currentTopic = currentTopicContent?.topic ? await getTopicById(currentTopicContent.topic) : null;
        setTimeout(() => {
            setAnimationClass('anim-exit-right');
            setTimeout(() => {
                navigate(`/dashboard/${currentTopic?.level_name}`);
            }, 300);
        }, 1000);
    };

    // Highlighting setup
    useEffect(() => {
        const handleSelectionChange = () => {
            const tooltip = highlightTooltipRef.current;
            if (!tooltip) return;

            const selection = window.getSelection();
            const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

            if (range && !selection.isCollapsed && qTextRef.current && qTextRef.current.contains(range.commonAncestorContainer)) {
                const rect = range.getBoundingClientRect();
                tooltip.style.display = 'block';
                tooltip.style.top = (rect.top - 40) + 'px';
                tooltip.style.left = (rect.left + (rect.width / 2)) + 'px';
            } else {
                tooltip.style.display = 'none';
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    const applyHighlight = (e) => {
        e.preventDefault();
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const mark = document.createElement('mark');
        mark.className = 'highlight-yellow';
        try {
            range.surroundContents(mark);
            saveText(qTextRef.current.innerHTML);
        } catch (err) {
            console.log("Simple highlight only.");
        }

        highlightTooltipRef.current.style.display = 'none';
        selection.removeAllRanges();
    };

    // Render Helpers
    const renderMediaContainer = () => {
        const q = questions[currentIdx];
        const mediaSource = q.localMediaSrc || q.mediaSrc;
        if (!q.mediaType || q.mediaType === 'none' || !mediaSource) {
            return null;
        }

        const getDriveVideoPreviewUrl = (url) => {
            const fileIdMatch = url.match(/\/file\/d\/([^/]+)/) || url.match(/[?&]id=([^&]+)/);
            if (fileIdMatch && fileIdMatch[1]) {
                return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
            }
            return url;
        };

        const getOneDriveVideoPreviewUrl = (url) => {
            const residMatch = url.match(/[?&]resid=([^&]+)/i);
            const authKeyMatch = url.match(/[?&]authkey=([^&]+)/i);

            if (residMatch && residMatch[1]) {
                const resid = decodeURIComponent(residMatch[1]);
                const authKey = authKeyMatch?.[1] ? `&authkey=${decodeURIComponent(authKeyMatch[1])}` : '';
                return `https://onedrive.live.com/embed?resid=${encodeURIComponent(resid)}${authKey}&em=2`;
            }

            return url;
        };

        if (q.mediaType === 'image') {
            return <img src={mediaSource} alt="Question media" />;
        }

        if (q.mediaType === 'video') {
            let src = mediaSource;
            const isYoutube = !q.mediaIsUpload && (src.includes('youtube.com') || src.includes('youtu.be'));
            const isGoogleDrive = !q.mediaIsUpload && (src.includes('drive.google.com') || src.includes('docs.google.com'));

            if (isYoutube) {
                if (src.includes('watch?v=')) src = src.replace('watch?v=', 'embed/');
                if (src.includes('youtu.be/')) src = src.replace('youtu.be/', 'www.youtube.com/embed/');

                return (
                    <iframe
                        src={src}
                        title={t('exam.youtubePreview')}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                );
            }

            if (isGoogleDrive) {
                return (
                    <iframe
                        src={getDriveVideoPreviewUrl(src)}
                        title={t('exam.drivePreview')}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                    ></iframe>
                );
            }

            const isOneDrive = !q.mediaIsUpload && (src.includes('onedrive.live.com') || src.includes('1drv.ms'));

            if (isOneDrive) {
                return (
                    <iframe
                        src={getOneDriveVideoPreviewUrl(src)}
                        title={t('exam.onedrivePreview')}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                    ></iframe>
                );
            }

            return <Video key={`${q.id}-${mediaSource}`} videoPath={mediaSource} />;
        }

        return null;
    };

    const navigate_module = async () => {
        if (isEditing || isReviewing) {
            setAnimationClass('anim-exit-right');
            const currentTopicContent = selectedTest?.topic_content ? await getTopicContentById(selectedTest.topic_content) : null;
            const currentTopic = currentTopicContent?.topic ? await getTopicById(currentTopicContent.topic) : null;
            setTimeout(() => {
                navigate(`/dashboard/${currentTopic?.level_name}`);
            }, 300);
            return;
        }

        setShowFsWarning(true);

    }

    const isLowTime = timeRemainingSeconds <= 60 && timeRemainingSeconds > 0;
    const showInitialLoading = isLoadingQuestions;
    const showNoTestSelected = !isLoadingQuestions && !selectedTest;
    const showNoQuestions = !isLoadingQuestions && selectedTest && questions.length === 0;

    return (
        showInitialLoading ? (
            <div className={`Test ${animationClass}`}>
                <Loading message={t('exam.loadingQuestions', { typeLower: typeLabelLower })} />
            </div>
        ) : showNoTestSelected ? (
            <div className={`Test ${animationClass}`}>
                {t('exam.noneSelected', { typeLower: typeLabelLower })}
            </div>
        ) : showNoQuestions ? (
            <div className={`Test ${animationClass}`}>
                {t('exam.noQuestionsAvailable', { typeLower: typeLabelLower })}
            </div>
        ) : (
        <div className={`Test ${animationClass}`}>
            {/* Time Warning Toast */}
            <div className={`time-warning-toast ${showTimeWarning ? 'show' : ''}`}>
                <svg style={{ width: '20px', height: '20px', fill: 'white' }} viewBox="0 0 24 24">
                    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                </svg>
                <span>{timeWarningText}</span>
            </div>

            {/* Start Test Overlay */}
            {showStartOverlay && (
                <div className="start-screen-overlay">
                    <h1>{t('exam.startScreenTitle', { type: typeLabel })}</h1>
                    <p>{t('exam.startScreenDesc', { typeLower: typeLabelLower })}</p>
                    <button className="btn-start-Test" onClick={startTest}>{t('modalExam.startTest')}</button>
                </div>
            )}

            {/* Top Bar */}
            <div className="top-bar">
                <div className="brand">{t('exam.brand', { type: typeLabel })}</div>

                <div className="progress-wrapper">
                    <div className="progress-info">
                        <span>{t('exam.progress')}</span>
                        <span>{t('exam.questionOf', { current: currentIdx + 1, total: questions.length })}</span>
                    </div>
                    <div className="segments-track">
                        {questions.map((question, idx) => {
                            let segmentClass = 'segment';
                            if (question.isFlagged) segmentClass += ' flagged';
                            else if (userAnswers[idx] && userAnswers[idx].length > 0) segmentClass += ' answered';
                            if (idx === currentIdx) segmentClass += ' active';

                            return (
                                <div
                                    key={idx}
                                    className={segmentClass}
                                    title={question.isFlagged ? t('exam.flaggedForReview') : ""}
                                    onClick={() => setCurrentIdx(idx)}
                                ></div>
                            );
                        })}
                    </div>
                </div>

                <div className="top-controls">
                    <div className="editor-timer-config">
                        {t('exam.timeLimit')}
                        <input
                            type="number"
                            value={timeLimitMin}
                            onChange={(e) => setTimeLimitMin(parseInt(e.target.value) || 0)}
                            min="0"
                        /> {t('exam.minSuffix')}
                        <input
                            type="number"
                            value={timeLimitSec}
                            onChange={(e) => setTimeLimitSec(parseInt(e.target.value) || 0)}
                            min="0"
                            max="59"
                        /> {t('exam.secSuffix')}
                    </div>

                    <div className={`student-timer-display ${isLowTime ? 'low-time' : ''}`}>
                        ⏱ <span>{formatTime(timeRemainingSeconds)}</span>
                    </div>

                    {!isReviewing && (
                        <button className={`btn-flag-toggle ${q.isFlagged ? 'active' : ''}`} onClick={toggleFlag}>
                            <svg viewBox="0 0 24 24"><path d="M14.4,6L14,4H5V21H7V14H12L12.4,16H19V6H14.4M17,14H14V12H10V6H12.4L12.8,8H17V14Z" /></svg>
                            {q.isFlagged ? t('exam.flagged') : t('exam.flag')}
                        </button>
                    )}
                    {isStaff() && !isReviewing && !isOnlyEditing && !isMobileOrTablet && (
                        <div className="edit-toggle-container">
                            <span className="switch-label">{t('exam.editorMode')}</span>
                            <div className={`toggle-switch ${isEditing ? 'active' : ''}`} onClick={toggleEditMode}></div>
                        </div>
                    )}
                </div>

                {isStaff() && !isReviewing && (
                    <button className="nav-btn-primary editor-controls" onClick={addNewQuestion} style={{ padding: '6px 14px', fontSize: '0.8rem', marginLeft: '10px' }}>{t('exam.newQuestionBtn')}</button>
                )}
            </div>

            {/* Main Stage */}
            <div className="main-stage">
                <div className={`question-wrapper layout-${q.layout}`} id="wrapper">
                    {/* Nav History Controls */}
                    <div className="nav-history-controls-vertical" style={{ display: isStaff() && isEditing && !isReviewing ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', gap: '12px', position: 'fixed', top: '100px', right: '25px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '12px 5px', zIndex: 1000 }}>
                        <button className="history-btn save-btn" onClick={saveTestSession} title={t('navbar.save')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                        </button>
                        <button className="history-btn" onClick={() => setIsHistoryModalOpen(true)} title={t('navbar.history')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                        </button>
                        <button className="history-btn" onClick={handleUndo} disabled={sessionHistoryMeta.index <= 0} title={t('navbar.undo')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path></svg>
                        </button>
                        <button className="history-btn" onClick={handleRedo} disabled={sessionHistoryMeta.index < 0 || sessionHistoryMeta.index >= sessionHistoryMeta.length - 1} title={t('navbar.redo')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"></path></svg>
                        </button>
                    </div>

                    {/* Support chat launcher. Sits directly under .nav-history-controls-vertical when
                        those editor controls are showing, and takes their slot when they are not --
                        both are fixed to the same right rail. */}
                    <button
                        className="support-chat-btn"
                        onClick={() => setIsSupportChatOpen((open) => !open)}
                        title={t('chat.openSupportChat')}
                        style={{ top: isStaff() && isEditing && !isReviewing ? '300px' : '100px' }}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            aria-hidden="true"
                            focusable="false"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-4.1-1.05L3 20l1.05-4.4A8.4 8.4 0 0 1 3 11.5a8.5 8.5 0 1 1 17 0Z" />
                            <path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01" />
                        </svg>
                    </button>
                    {/* Toolbar */}
                    <div className="q-toolbar">
                        <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>{t('exam.layout')}</span>
                        <button className={`tool-btn ${q.layout === 'center' ? 'active' : ''}`} onClick={() => updateProp('layout', 'center')}>{t('exam.center')}</button>
                        <button className={`tool-btn ${q.layout === 'split' ? 'active' : ''}`} onClick={() => updateProp('layout', 'split')}>{t('exam.split')}</button>

                        <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 8px' }}></div>

                        <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>{t('exam.type')}</span>
                        <button className={`tool-btn ${q.type === 'single' ? 'active' : ''}`} onClick={() => updateProp('type', 'single')}>{t('exam.single')}</button>
                        <button className={`tool-btn ${q.type === 'multiple' ? 'active' : ''}`} onClick={() => updateProp('type', 'multiple')}>{t('exam.multi')}</button>

                        <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 8px' }}></div>

                        <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>{t('exam.media')}</span>
                        <button className={`tool-btn ${!q.mediaType || q.mediaType === 'none' ? 'active' : ''}`} onClick={() => updateMedia('none')}>{t('exam.none')}</button>
                        <button className={`tool-btn ${q.mediaType === 'image' ? 'active' : ''}`} onClick={() => updateMedia('image')}>{t('exam.image')}</button>
                        <button className={`tool-btn ${q.mediaType === 'video' ? 'active' : ''}`} onClick={() => updateMedia('video')}>{t('exam.video')}</button>

                        <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 8px' }}></div>
                        <button className="tool-btn btn-delete-q" onClick={deleteCurrentQuestion}>{t('exam.deleteBtn')}</button>
                    </div>

                    {/* Media Input Group */}
                    {isEditing && q.mediaType && q.mediaType !== 'none' && (
                        <div className="media-input-group">
                            <div className="media-input-row">
                                <span style={{ fontWeight: '600', color: '#b45309' }}>{t('exam.urlLabel')}</span>
                                <input
                                    type="text"
                                    className="media-input"
                                    placeholder={t('exam.pasteLinkPlaceholder')}
                                    value={q.mediaSrc}
                                    onChange={(e) => saveMediaUrl(e.target.value)}
                                />
                            </div>
                            <div className="media-divider">{t('exam.orDivider')}</div>
                            <div className="media-input-row">
                                <span style={{ fontWeight: '600', color: '#b45309' }}>{t('exam.uploadLabel')}</span>
                                <input
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={(e) => handleFileUpload(e.target.files[0])}
                                />
                            </div>
                        </div>
                    )}

                    {/* Question Container */}
                    <div className="q-inner">
                        <div className="media-display">
                            {renderMediaContainer()}
                        </div>
                        {(q.localMediaSrc || q.mediaSrc) && (
                            <span className="media-source-link">
                                {t('exam.sourceLabel', { filename: (q.localMediaSrc || q.mediaSrc).includes('://') ? new URL(q.localMediaSrc || q.mediaSrc).pathname.split('/').pop() || 'Media File' : (q.localMediaSrc || q.mediaSrc).split('/').pop() })}
                            </span>
                        )}

                        <div
                            ref={qTextRef}
                            className="q-text"
                            contentEditable={isEditing && !isReviewing}
                            onBlur={(e) => saveText(e.currentTarget.innerHTML)}
                            dangerouslySetInnerHTML={{ __html: q.text }}
                        ></div>

                        <div
                            ref={highlightTooltipRef}
                            id="highlightTooltip"
                            onMouseDown={applyHighlight}
                            style={{ display: 'none' }}
                        >
                            {t('exam.highlightBtn')}
                        </div>

                        <div>
                            <div className="options-list">
                                {q.options.map((opt, oIdx) => {
                                    const isSelected = isReviewing
                                        ? q.userSelectedChoiceId === opt.choiceId
                                        : userAnswers[currentIdx]?.includes(oIdx);
                                    const isMarkedCorrect = isReviewing ? opt.isCorrect : (isEditing && opt.isCorrect);
                                    const isEliminated = opt.eliminated;

                                    let optionClass = 'option-item';
                                    if (isSelected) optionClass += ' selected';
                                    if (isMarkedCorrect) optionClass += ' correct-mark';
                                    if (isEliminated) optionClass += ' eliminated';

                                    return (
                                        <div
                                            key={oIdx}
                                            className={optionClass}
                                        >
                                            <div className="opt-indicator"></div>
                                            <div
                                                className="opt-text-content"
                                                contentEditable={isEditing && !isReviewing}
                                                suppressContentEditableWarning
                                                onClick={() => {
                                                    if (!isEliminated && !isEditing && !isReviewing) {
                                                        toggleUserAnswer(oIdx);
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (isEditing && !isReviewing) {
                                                        saveChoiceText(oIdx, e.currentTarget.innerText);
                                                    }
                                                }}
                                            >
                                                {opt.text}
                                            </div>
                                            {isReviewing && isSelected && (
                                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', fontWeight: 700, color: '#2563eb' }}>{t('exam.yourChoice')}</span>
                                            )}
                                            {isReviewing && isMarkedCorrect && (
                                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', fontWeight: 700, color: '#16a34a' }}>{t('exam.correctChoice')}</span>
                                            )}
                                            <button
                                                className="btn-eliminate"
                                                title={t('exam.eliminateTooltip')}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleEliminate(oIdx);
                                                }}
                                                style={{ display: isReviewing ? 'none' : 'inline-flex' }}
                                            >
                                                Θ
                                            </button>
                                            <div className="opt-actions">
                                                <button
                                                    className={`mini-btn btn-mark-correct ${opt.isCorrect ? 'is-correct' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleCorrectAnswer(oIdx);
                                                    }}
                                                    style={{ display: isReviewing ? 'none' : 'inline-flex' }}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="mini-btn btn-delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteOption(oIdx);
                                                    }}
                                                    style={{ display: isReviewing ? 'none' : 'inline-flex' }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {!isReviewing && <button className="btn-add-option" onClick={addOption}>{t('exam.addOption')}</button>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Nav */}
            <div className="footer-nav">
                <div className="footer-content">
                    <div className="nav-row">
                        <button className="nav-btn nav-btn-secondary" onClick={() => navigate_question(-1)}>{t('exam.previous')}</button>
                        {currentIdx !== questions.length - 1 && (
                            <button className="nav-btn nav-btn-primary" onClick={() => navigate_question(1)}>{t('exam.nextQuestion')}</button>
                        )}
                    </div>
                    <button className="dotted-trigger" onClick={openMap}>
                        {t('exam.viewQuestions')}
                    </button>
                </div>

                <div className="footer-buttons">
                    <button className="btn-primary" onClick={() => navigate_module()}>
                        {t('exam.returnToModule')}
                    </button>
                    {/* Submit Button */}
                    {!isEditing && !isReviewing && (
                        <button
                            className={`btn-submit-Test ${getAnsweredCount() === questions.length || currentIdx === questions.length - 1 ? 'pulse-anim' : ''}`}
                            onClick={confirmSubmit}
                        >
                            {t('exam.submitType', { type: typeLabel, typeLower: typeLabelLower })}
                        </button>
                    )}
                </div>
            </div>

            {/* Map Overlay */}
            {showMapOverlay && (
                <div className="overlay visible" onClick={closeMap}>
                    <div className="map-box" onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>{t('exam.questionNavigator')}</h3>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', fontSize: '0.8rem', color: '#64748b' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <div style={{ width: '10px', height: '10px', background: '#ddd6fe', borderRadius: '2px' }}></div> {t('exam.answeredLegend')}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <div style={{ width: '10px', height: '10px', background: '#fef08a', border: '1px solid #fde047', borderRadius: '2px' }}></div> {t('exam.flaggedLegend')}
                            </span>
                        </div>
                        <div className="grid">
                            {questions.map((question, idx) => {
                                let cellClass = 'grid-cell';
                                if (idx === currentIdx) cellClass += ' current';
                                else if (question.isFlagged) cellClass += ' flagged';
                                else if (userAnswers[idx] && userAnswers[idx].length > 0) cellClass += ' answered';

                                return (
                                    <div
                                        key={idx}
                                        className={cellClass}
                                        onClick={() => jumpToQuestion(idx)}
                                    >
                                        {idx + 1}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Warning */}
            {showFsWarning && (
                <div className="fs-warning-overlay visible">
                    <div className="fs-warning-box">
                        <button className="fs-close-x" onClick={cancelFirstWarning}>×</button>
                        <h3>{t('exam.exitTypeTitle', { typeLower: typeLabelLower })}</h3>
                        <p>{t('exam.exitTypeWarning', { typeLower: typeLabelLower, points: selectedTest.max_score * 0.1 })}</p>
                        <div className="fs-warning-actions">
                            <button className="fs-btn fs-btn-cancel" onClick={cancelFirstWarning}>{t('exam.returnToType', { typeLower: typeLabelLower })}</button>
                            <button className="fs-btn fs-btn-confirm" onClick={triggerSecondWarning}>{t('exam.proceedToExit')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Second Warning */}
            {showFsSecondWarning && (
                <div className="fs-warning-overlay visible">
                    <div className="fs-warning-box boom">
                        <h3>{t('exam.finalConfirmationTitle')}</h3>
                        <p>{t('exam.finalConfirmationWarning', { points: selectedTest.max_score * 0.1 })}</p>
                        <div className="fs-warning-actions">
                            <button className="fs-btn fs-btn-cancel" onClick={cancelSecondWarning}>{t('exam.returnToType', { typeLower: typeLabelLower })}</button>
                            <button className="fs-btn fs-btn-confirm" style={{ background: '#fee2e2', color: '#ef4444' }} onClick={() => triggerEndTest('penalty')}>{t('exam.acknowledgeExit')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Submit Confirmation */}
            {showSubmitConfirm && (
                <div className="fs-warning-overlay visible">
                    <div className="fs-warning-box">
                        <button className="fs-close-x" onClick={cancelSubmit}>×</button>
                        <h3>{t('exam.submitType', { type: typeLabel, typeLower: typeLabelLower })}</h3>
                        <div dangerouslySetInnerHTML={{ __html: submitConfirmContent }}></div>
                        <div className="fs-warning-actions">
                            <button className="fs-btn fs-btn-confirm" onClick={cancelSubmit}>{t('common.cancel')}</button>
                            <button className="fs-btn fs-btn-cancel" style={{ background: '#22c55e' }} onClick={() => triggerEndTest('submit')}>{t('exam.yesSubmit')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* End Test Screen */}
            {showEndScreen && (
                <div className="closed-screen visible">
                    <h1>{endScreenData.title}</h1>
                    <p>{endScreenData.subtitle}</p>
                    {endScreenData.penalty && (
                        <div className="penalty-display" style={{ display: 'inline-block' }}>
                            {t('exam.penaltyApplied', { points: selectedTest.max_score * 0.1 })}
                        </div>
                    )}
                    {endScreenData.score && (
                        <div className="score-display" style={{ display: 'inline-block' }}>
                            {endScreenData.score}
                        </div>
                    )}
                    <div className="loading-container">
                        {t('exam.goingBackToModule')}<span className="loading-dots"></span>
                    </div>
                </div>
            )}

            <ModalEditorHistory
                testId={selectedTest?.id}
                isOpen={isHistoryModalOpen}
                closeModal={() => setIsHistoryModalOpen(false)}
            />

            <SavingIndicator active={isSavingTest} label={t('common.saving')} />
            <Toast toast={toast} />
            <Chat
                isOpen={isSupportChatOpen}
                onToggle={() => setIsSupportChatOpen((open) => !open)}
                // The 💬 button above is this page's way in; Chat's own floating pill
                // would just duplicate it.
                showToggleButton={false}
            />
        </div>
        )
    );
}

export default Test;
